import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "tailwindcss/tailwind.css";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState, useCallback } from "react";
import {
  getVitalTrend,
  VitalTrendIndicator,
} from "./icons/patientDetailIcons";
import { PageLoading } from "./loading/LoadingIndicators";
import { MESSAGES } from "../constants/app";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const heartRateOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#373F51",
      font: { weight: "normal" as const, size: 10 },
      formatter: function (value: number) {
        return value;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#4B5363",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 0,
      max: 200,
      ticks: {
        stepSize: 50,
        display: false,
      },
      display: true,
    },
  },
};

const bloodPressureOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#373F51",
      font: { weight: "normal" as const, size: 10 },
      formatter: function (value: any) {
        if (Array.isArray(value)) {
          return `${value[1]}/${value[0]}`;
        }
        return value;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#4B5363",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 0,
      max: 200,
      ticks: {
        stepSize: 50,
        display: false,
      },
      display: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

const bloodOxygenOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: false,
    },
    tooltip: { enabled: false },
    datalabels: {
      display: true,
      align: "top" as const,
      anchor: "end" as const,
      color: "#373F51",
      font: { weight: "normal" as const, size: 10 },
      formatter: function (value: number) {
        return `${value}%`;
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      display: true,
      ticks: {
        color: "#4B5363",
        font: { size: 10 },
      },
    },
    y: {
      grid: {
        color: "#e5e7eb",
        borderDash: [10, 10],
        drawBorder: false,
      },
      min: 70,
      max: 105,
      ticks: {
        stepSize: 10,
        callback: (value: number | string) => `${value}%`,
        display: false,
      },
      display: true,
    },
  },
  elements: {
    bar: {
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
    },
  },
};

interface HealthMetricsDashboardProps {
  vitals: any;
  heartRateDataForChart: number[];
  setHeartRateDataForChart: (data: number[]) => void;
  bloodPressureDataForChart: [number, number][];
  setBloodPressureDataForChart: (data: [number, number][]) => void;
  bloodOxygenDataForChart: number[];
  setBloodOxygenDataForChart: (data: number[]) => void;
  heartRateTimeLabels: string[];
  setHeartRateTimeLabels: (labels: string[]) => void;
  bloodPressureTimeLabels: string[];
  setBloodPressureTimeLabels: (labels: string[]) => void;
  bloodOxygenTimeLabels: string[];
  setBloodOxygenTimeLabels: (labels: string[]) => void;
  latestHeartRate: number | null;
  setLatestHeartRate: (value: number | null) => void;
  latestBloodPressure: { systolic: number; diastolic: number } | null;
  setLatestBloodPressure: (
    value: { systolic: number; diastolic: number } | null
  ) => void;
  latestBloodOxygen: number | null;
  setLatestBloodOxygen: (value: number | null) => void;
  latestHeartRateTime: string;
  setLatestHeartRateTime: (value: string) => void;
  latestBloodPressureTime: string;
  setLatestBloodPressureTime: (value: string) => void;
  latestBloodOxygenTime: string;
  setLatestBloodOxygenTime: (value: string) => void;
}

export default function HealthMetricsDashboard({
  vitals,
  heartRateDataForChart,
  setHeartRateDataForChart,
  bloodPressureDataForChart,
  setBloodPressureDataForChart,
  bloodOxygenDataForChart,
  setBloodOxygenDataForChart,
  heartRateTimeLabels,
  setHeartRateTimeLabels,
  bloodPressureTimeLabels,
  setBloodPressureTimeLabels,
  bloodOxygenTimeLabels,
  setBloodOxygenTimeLabels,
  latestHeartRate,
  setLatestHeartRate,
  latestBloodPressure,
  setLatestBloodPressure,
  latestBloodOxygen,
  setLatestBloodOxygen,
  latestHeartRateTime,
  setLatestHeartRateTime,
  latestBloodPressureTime,
  setLatestBloodPressureTime,
  latestBloodOxygenTime,
  setLatestBloodOxygenTime,
}: HealthMetricsDashboardProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to format timestamp
  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }, []);

  // Helper function to format timestamp for chart labels (first label has AM/PM, others don't)
  const formatTimestampForChart = useCallback((
    timestamp: string,
    isFirst: boolean = false
  ) => {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const displayHours = hours % 12 || 12;
    const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;

    if (isFirst) {
      const ampm = hours >= 12 ? "pm" : "am";
      return `${displayHours}${displayMinutes} ${ampm}`;
    } else {
      return `${displayHours}${displayMinutes}`;
    }
  }, []);

  // Helper function to generate padding time labels when fewer than 6 records exist
  const generatePaddingTimeLabels = useCallback((
    existingTimestamps: string[],
    targetCount: number = 6
  ): string[] => {
    if (existingTimestamps.length === 0) {
      // If no existing timestamps, generate labels based on current time (newest to oldest)
      const now = new Date();
      const labels = [];
      // Generate labels from newest (current time) to oldest (going back in time)
      for (let i = 0; i < targetCount; i++) {
        const time = new Date(now.getTime() - i * 15 * 60 * 1000);
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const displayHours = hours % 12 || 12;
        const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;
        if (i === 0) {
          // First label (newest) should have AM/PM
          const ampm = hours >= 12 ? "pm" : "am";
          labels.push(`${displayHours}${displayMinutes} ${ampm}`);
        } else {
          labels.push(`${displayHours}${displayMinutes}`);
        }
      }
      return labels;
    }

    // If we have existing timestamps, generate labels based on the pattern
    const labels: string[] = [];
    const existingCount = existingTimestamps.length;
    const paddingCount = targetCount - existingCount;

    // Sort existing timestamps in descending order (newest first)
    const sortedTimestamps = [...existingTimestamps].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime()
    );

    // Add existing timestamps FIRST in reverse chronological order (newest first)
    // The first existing timestamp (newest) should have AM/PM if it's the first label overall
    sortedTimestamps.forEach((timestamp, index) => {
      const isFirstLabel = index === 0;
      labels.push(formatTimestampForChart(timestamp, isFirstLabel));
    });

    // Calculate average interval between existing timestamps for padding
    let avgInterval = 15 * 60 * 1000; // Default to 15 minutes
    if (sortedTimestamps.length > 1) {
      const intervals: number[] = [];
      // Calculate intervals going backwards (from newest to oldest)
      for (let i = 1; i < sortedTimestamps.length; i++) {
        intervals.push(
          new Date(sortedTimestamps[i - 1]).getTime() -
            new Date(sortedTimestamps[i]).getTime()
        );
      }
      avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    } else if (sortedTimestamps.length === 1) {
      // If only one timestamp, use 15-minute intervals for padding
      avgInterval = 15 * 60 * 1000;
    }

    // Generate padding labels AFTER the existing timestamps (going backwards in time from oldest)
    const oldestTimestamp = new Date(
      sortedTimestamps[sortedTimestamps.length - 1]
    );
    for (let i = 1; i <= paddingCount; i++) {
      const time = new Date(oldestTimestamp.getTime() - i * avgInterval);
      const hours = time.getHours();
      const minutes = time.getMinutes();
      const displayHours = hours % 12 || 12;
      const displayMinutes = `:${minutes.toString().padStart(2, "0")}`;
      labels.push(`${displayHours}${displayMinutes}`);
    }

    return labels;
  }, [formatTimestampForChart]);

  useEffect(() => {
    setIsLoading(true);

    // Helper functions to process vitals data
    const getLatestVitalFromData = (bodyType: string) => {
      if (!vitals) return undefined;
      let all: any[] = [];
      Object.values(vitals).forEach((arr: any) => {
        if (Array.isArray(arr)) {
          all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
        }
      });
      if (all.length === 0) return undefined;
      all.sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );
      return all[0];
    };

    const getLatestVitalsForChartFromData = (bodyType: string) => {
      if (!vitals) return [];
      let all: any[] = [];
      Object.values(vitals).forEach((arr: any) => {
        if (Array.isArray(arr)) {
          all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
        }
      });
      if (all.length === 0) return [];
      all.sort(
        (a: any, b: any) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );
      return all.slice(0, 6); // Get latest 5 records
    };

    // Update latest values
    const bpVital = getLatestVitalFromData("BLOOD_PRESSURE");
    const hrVital = getLatestVitalFromData("HEART_RATE");
    const boVital = getLatestVitalFromData("BLOOD_OXYGEN");

    if (
      bpVital &&
      bpVital.body &&
      bpVital.body.systolic !== undefined &&
      bpVital.body.diastolic !== undefined
    ) {
      setLatestBloodPressure({
        systolic: bpVital.body.systolic,
        diastolic: bpVital.body.diastolic,
      });
    } else {
      setLatestBloodPressure(null);
    }

    if (hrVital && hrVital.body && hrVital.body.value !== undefined) {
      setLatestHeartRate(hrVital.body.value);
    } else {
      setLatestHeartRate(null);
    }

    if (boVital && boVital.body && boVital.body.value !== undefined) {
      setLatestBloodOxygen(boVital.body.value);
    } else {
      setLatestBloodOxygen(null);
    }

    // Update chart data
    const heartRateVitals = getLatestVitalsForChartFromData("HEART_RATE");
    const bloodPressureVitals =
      getLatestVitalsForChartFromData("BLOOD_PRESSURE");
    const bloodOxygenVitals =
      getLatestVitalsForChartFromData("BLOOD_OXYGEN");

    // Extract heart rate data and timestamps
    const heartRateData = heartRateVitals.map((vital) => vital.body.value);
    const heartRateTimestamps = heartRateVitals.map(
      (vital) => vital.created_at
    );
    // Pad data to 6 items - actual entries first, then nulls
    const paddedHeartRateData = [...heartRateData];
    while (paddedHeartRateData.length < 6) {
      paddedHeartRateData.push(null as any);
    }
    const heartRateLabels = generatePaddingTimeLabels(
      heartRateTimestamps,
      6
    );
    setHeartRateDataForChart(paddedHeartRateData);
    setHeartRateTimeLabels(heartRateLabels);

    // Extract blood pressure data and timestamps
    const bloodPressureData = bloodPressureVitals.map(
      (vital) =>
        [vital.body.diastolic, vital.body.systolic] as [number, number]
    );
    const bloodPressureTimestamps = bloodPressureVitals.map(
      (vital) => vital.created_at
    );
    // Pad data to 6 items - actual entries first, then nulls
    const paddedBloodPressureData = [...bloodPressureData];
    while (paddedBloodPressureData.length < 6) {
      paddedBloodPressureData.push([null, null] as any);
    }
    const bloodPressureLabels = generatePaddingTimeLabels(
      bloodPressureTimestamps,
      6
    );
    setBloodPressureDataForChart(paddedBloodPressureData);
    setBloodPressureTimeLabels(bloodPressureLabels);

    // Extract blood oxygen data and timestamps
    const bloodOxygenData = bloodOxygenVitals.map(
      (vital) => vital.body.value
    );
    const bloodOxygenTimestamps = bloodOxygenVitals.map(
      (vital) => vital.created_at
    );
    // Pad data to 6 items - actual entries first, then nulls
    const paddedBloodOxygenData = [...bloodOxygenData];
    while (paddedBloodOxygenData.length < 6) {
      paddedBloodOxygenData.push(null as any);
    }
    const bloodOxygenLabels = generatePaddingTimeLabels(
      bloodOxygenTimestamps,
      6
    );
    setBloodOxygenDataForChart(paddedBloodOxygenData);
    setBloodOxygenTimeLabels(bloodOxygenLabels);

    // Set the latest reading times from actual timestamps
    if (hrVital && hrVital.created_at) {
      setLatestHeartRateTime(formatTimestamp(hrVital.created_at));
    } else {
      setLatestHeartRateTime("");
    }
    if (bpVital && bpVital.created_at) {
      setLatestBloodPressureTime(formatTimestamp(bpVital.created_at));
    } else {
      setLatestBloodPressureTime("");
    }
    if (boVital && boVital.created_at) {
      setLatestBloodOxygenTime(formatTimestamp(boVital.created_at));
    } else {
      setLatestBloodOxygenTime("");
    }

    setIsLoading(false);
  }, [
    formatTimestamp,
    generatePaddingTimeLabels,
    setBloodOxygenDataForChart,
    setBloodOxygenTimeLabels,
    setBloodPressureDataForChart,
    setBloodPressureTimeLabels,
    setHeartRateDataForChart,
    setHeartRateTimeLabels,
    setLatestBloodOxygen,
    setLatestBloodOxygenTime,
    setLatestBloodPressure,
    setLatestBloodPressureTime,
    setLatestHeartRate,
    setLatestHeartRateTime,
    vitals,
  ]);

  const bloodPressure = latestBloodPressure
    ? `${latestBloodPressure.systolic}/${latestBloodPressure.diastolic}`
    : null;
  // Check if data is available - both chart data and latest value must exist
  const hasHeartRateData =
    heartRateDataForChart.length > 0 && latestHeartRate !== null;
  const hasBloodPressureData =
    bloodPressureDataForChart.length > 0 && latestBloodPressure !== null;
  const hasBloodOxygenData =
    bloodOxygenDataForChart.length > 0 && latestBloodOxygen !== null;
  // Use actual timestamp labels from the provided vitals data.

  // Create chart data with latest values
  const heartRateData = {
    labels:
      heartRateTimeLabels.length > 0 ? heartRateTimeLabels : Array(6).fill(""),
    datasets: [
      {
        label: "Heart Rate",
        data: heartRateDataForChart,
        borderColor: "#F43F5E",
        backgroundColor: "rgba(244,63,94,0.08)",
        tension: 0.1,
        fill: true,
        pointRadius: 4,
        pointBackgroundColor: "#FFFFFF",
        pointBorderColor: "#F43F5E",
        pointBorderWidth: 2,
        datalabels: {
          align: "top" as const,
          anchor: "end" as const,
          color: "#373F51",
          font: { weight: "normal" as const, size: 10 },
          formatter: function (value: number) {
            return value;
          },
          display: true,
        },
      },
    ],
  };

  const bloodPressureData = {
    labels:
      bloodPressureTimeLabels.length > 0
        ? bloodPressureTimeLabels
        : Array(6).fill(""),
    datasets: [
      {
        label: "Blood Pressure",
        data: bloodPressureDataForChart,
        backgroundColor: bloodPressureDataForChart.map(
          ([diastolic, systolic]) =>
            systolic >= 140 || diastolic >= 90
              ? "#FB7185"
              : systolic >= 120 || diastolic >= 80
              ? "#FB7185"
              : "#2DD4BF"
        ),
        borderColor: bloodPressureDataForChart.map(([diastolic, systolic]) =>
          systolic >= 140 || diastolic >= 90
            ? "#FB7185"
            : systolic >= 120 || diastolic >= 80
            ? "#FB7185"
            : "#2DD4BF"
        ),
        borderWidth: 1,
        barThickness: 32,
        maxBarThickness: 40,
      },
    ],
  };

  const bloodOxygenData = {
    labels:
      bloodOxygenTimeLabels.length > 0
        ? bloodOxygenTimeLabels
        : Array(6).fill(""),
    datasets: [
      {
        label: "Blood Oxygen",
        data: bloodOxygenDataForChart, // Use latest blood oxygen as last value
        backgroundColor: bloodOxygenDataForChart.map((value) =>
          value < 90 ? "#FB7185" : value < 95 ? "#FB7185" : "#2DD4BF"
        ),
        borderColor: bloodOxygenDataForChart.map((value) =>
          value < 90 ? "#FB7185" : value < 95 ? "#FB7185" : "#2DD4BF"
        ),
        borderWidth: 1,
        barThickness: 32,
        maxBarThickness: 40,
      },
    ],
  };

  const isHeartRateAbnormal = (heartRate: number) =>
    heartRate < 60 || heartRate > 100;

  const isBloodPressureAbnormal = (systolic: number, diastolic: number) =>
    systolic >= 140 || diastolic >= 90;

  const isBloodOxygenAbnormal = (bloodOxygen: number) => bloodOxygen < 90;

  const getHeartRateColor = (heartRate: number) => {
    if (isHeartRateAbnormal(heartRate)) {
      return "text-vital-high";
    }
    if (heartRate < 70 || heartRate > 90) {
      return "text-vital-medium";
    }
    return "text-vital-low";
  };

  const getBloodPressureColor = (systolic: number, diastolic: number) => {
    if (isBloodPressureAbnormal(systolic, diastolic)) {
      return "text-vital-high";
    }
    if (systolic >= 120 || diastolic >= 80) {
      return "text-vital-medium";
    }
    return "text-vital-low";
  };

  const getBloodOxygenColor = (bloodOxygen: number) => {
    if (isBloodOxygenAbnormal(bloodOxygen)) {
      return "text-vital-high";
    }
    if (bloodOxygen < 95) {
      return "text-vital-medium";
    }
    return "text-vital-low";
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <PageLoading
        message={MESSAGES.LOADING.HEALTH_METRICS}
        messageClassName="text-lg text-guardian-header"
        className="w-full"
      />
    );
  }

  const heartRateTrend =
    latestHeartRate !== null
      ? getVitalTrend(
          heartRateDataForChart,
          latestHeartRate,
          isHeartRateAbnormal(latestHeartRate),
          latestHeartRate < 60
        )
      : null;

  const bloodPressureTrend =
    latestBloodPressure !== null
      ? getVitalTrend(
          bloodPressureDataForChart,
          latestBloodPressure.systolic,
          isBloodPressureAbnormal(
            latestBloodPressure.systolic,
            latestBloodPressure.diastolic
          ),
          false
        )
      : null;

  const bloodOxygenTrend =
    latestBloodOxygen !== null
      ? getVitalTrend(
          bloodOxygenDataForChart,
          latestBloodOxygen,
          isBloodOxygenAbnormal(latestBloodOxygen),
          true
        )
      : null;

  return (
    <div className="flex w-full flex-col gap-4 overflow-hidden">
      {/* Heart Rate Section */}
      <div className="overflow-hidden rounded-2xl border border-guardian-20 bg-white px-3">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-stretch">
          <div className="flex w-full shrink-0 flex-col border-guardian-10 sm:w-[175px] sm:border-r sm:pr-3">
            <div className="flex items-center gap-2 border-b border-guardian-10 pb-2.5 pt-3">
              <img
                src="/images/heart_rate.png"
                alt=""
                className="h-6 w-6"
                aria-hidden
              />
              <span className="text-base font-extrabold tracking-tight text-guardian-body">
                Heart Rate
              </span>
            </div>
            {hasHeartRateData && latestHeartRate !== null ? (
              <div className="flex flex-col py-3">
                <div className="flex items-end gap-1">
                  <span
                    className={`text-[28px] font-bold leading-[38px] tracking-tight ${getHeartRateColor(
                      latestHeartRate
                    )}`}
                  >
                    {latestHeartRate}
                  </span>
                  <span className="pb-1 text-sm font-bold text-guardian-header">
                    BPM
                  </span>
                  <VitalTrendIndicator direction={heartRateTrend} />
                </div>
                <div className="mt-1 text-sm text-guardian-body">
                  {latestHeartRateTime}
                </div>
              </div>
            ) : null}
          </div>

          <div className="h-56 w-full min-w-0 flex-1 sm:h-[157px] sm:py-2">
            {hasHeartRateData ? (
              <Line data={heartRateData} options={heartRateOptions} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-sm text-guardian-header">
                  No data available
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Pressure Section */}
      <div className="overflow-hidden rounded-2xl border border-guardian-20 bg-white px-3">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-stretch">
          <div className="flex w-full shrink-0 flex-col border-guardian-10 sm:w-[175px] sm:border-r sm:pr-3">
            <div className="flex items-center gap-2 border-b border-guardian-10 pb-2.5 pt-3">
              <img
                src="/images/blood_pressure.png"
                alt=""
                className="h-6 w-6"
                aria-hidden
              />
              <span className="text-base font-extrabold tracking-tight text-guardian-body">
                Blood Pressure
              </span>
            </div>
            {hasBloodPressureData &&
            latestBloodPressure !== null &&
            bloodPressure !== null ? (
              <div className="flex flex-col py-3">
                <div className="flex items-end gap-1">
                  <span
                    className={`text-[28px] font-bold leading-[38px] tracking-tight ${getBloodPressureColor(
                      latestBloodPressure.systolic,
                      latestBloodPressure.diastolic
                    )}`}
                  >
                    {bloodPressure}
                  </span>
                  <span className="pb-1 text-sm font-bold text-guardian-header">
                    mmHg
                  </span>
                  <VitalTrendIndicator direction={bloodPressureTrend} />
                </div>
                <div className="mt-1 text-sm text-guardian-body">
                  {latestBloodPressureTime}
                </div>
              </div>
            ) : null}
          </div>

          <div className="h-56 w-full min-w-0 flex-1 sm:h-[157px] sm:py-2">
            {hasBloodPressureData ? (
              <Bar data={bloodPressureData} options={bloodPressureOptions} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-sm text-guardian-header">
                  No data available
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blood Oxygen Section */}
      <div className="overflow-hidden rounded-2xl border border-guardian-20 bg-white px-3">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-stretch">
          <div className="flex w-full shrink-0 flex-col border-guardian-10 sm:w-[175px] sm:border-r sm:pr-3">
            <div className="flex items-center gap-2 border-b border-guardian-10 pb-2.5 pt-3">
              <img
                src="/images/blood_oxygen.png"
                alt=""
                className="h-6 w-6"
                aria-hidden
              />
              <span className="text-base font-extrabold tracking-tight text-guardian-body">
                Blood Oxygen
              </span>
            </div>
            {hasBloodOxygenData && latestBloodOxygen !== null ? (
              <div className="flex flex-col py-3">
                <div className="flex items-end gap-1">
                  <span
                    className={`text-[28px] font-bold leading-[38px] tracking-tight ${getBloodOxygenColor(
                      latestBloodOxygen
                    )}`}
                  >
                    {latestBloodOxygen}%
                  </span>
                  <VitalTrendIndicator direction={bloodOxygenTrend} />
                </div>
                <div className="mt-1 text-sm text-guardian-body">
                  {latestBloodOxygenTime}
                </div>
              </div>
            ) : null}
          </div>

          <div className="h-56 w-full min-w-0 flex-1 sm:h-[157px] sm:py-2">
            {hasBloodOxygenData ? (
              <Bar data={bloodOxygenData} options={bloodOxygenOptions} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="text-sm text-guardian-header">
                  No data available
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
